/**
 * 여러 줄 텍스트 입력 컴포넌트
 *
 * 디자인 시스템의 input-base 스타일을 활용한 재사용 가능한 텍스트 영역입니다.
 * AppInput과 유사한 인터페이스로 label, 에러 상태, 도움말 텍스트를 지원합니다.
 *
 * @component
 * @example
 * // 기본 텍스트 영역
 * <AppTextarea
 *   label="가사"
 *   placeholder="노래의 가사를 입력하세요"
 *   onChange={(e) => setLyrics(e.target.value)}
 * />
 *
 * @example
 * // 에러 상태
 * <AppTextarea
 *   label="설명"
 *   error={!!descError}
 *   helperText={descError || "최소 10글자 이상 입력하세요"}
 *   minLength={10}
 * />
 *
 * @typedef {object} AppTextareaProps
 * @property {string} [label] - 텍스트 영역 위에 표시할 라벨
 * @property {boolean} [error=false] - 에러 상태 (true일 때 빨간색으로 표시)
 * @property {string} [helperText] - 도움말 또는 에러 메시지
 * @property {string} [className] - 추가 CSS 클래스
 * @property {string} [id] - 텍스트 영역의 ID (생략 시 label에서 자동 생성)
 *
 * @returns {JSX.Element} 스타일이 적용된 여러 줄 입력 필드
 */

import { TextareaHTMLAttributes, forwardRef } from 'react';
import { cn } from '@/lib/utils';

export interface AppTextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  error?: boolean;
  helperText?: string;
  label?: string;
}

export const AppTextarea = forwardRef<HTMLTextAreaElement, AppTextareaProps>(
  ({ error, helperText, label, className, id, ...props }, ref) => {
    const textareaId = id || label?.toLowerCase().replace(/\s+/g, '-');
    const helperId = helperText && textareaId ? `${textareaId}-helper` : undefined;

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={textareaId}
            className="block text-sm font-medium text-neutral-700 mb-1"
          >
            {label}
          </label>
        )}
        <textarea
          ref={ref}
          id={textareaId}
          aria-invalid={error || undefined}
          aria-describedby={helperId}
          className={cn('input-base min-h-[100px] resize-y', error && 'input-error', className)}
          {...props}
        />
        {helperText && (
          <p
            id={helperId}
            className={cn('text-sm mt-1', error ? 'text-error-600' : 'text-neutral-500')}
            role={error ? 'alert' : undefined}
          >
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

AppTextarea.displayName = 'AppTextarea';
